import { Box, Button, Card, CardContent, Divider, Stack, Typography } from '@mui/material';
import CheckIcon from '@mui/icons-material/Check';
import { useTranslation } from 'react-i18next';
import type { PlanInterval, SubscriptionPlan } from '@/entities/subscription';
import { calcPromoValues } from '../model/calcPromoValues';
import { formatCurrency } from '../model/formatCurrency';
import { groupFeatures } from '../model/groupFeatures';
import { FirstYearPromoCallout } from './FirstYearPromoCallout';

type FeatureItem = {
  name: string;
  description?: string;
  group?: string;
};

type Props = {
  plan: SubscriptionPlan;
  interval: PlanInterval;
  previousTierLabel: string;
  audienceTagline: string;
  features: FeatureItem[];
  variant: 'from-free' | 'from-pro';
  ctaLabel: string;
  onUpgrade: () => void;
  upgradeLoading?: boolean;
};

const capitalize = (s: string): string => s.charAt(0).toUpperCase() + s.slice(1);

const getEverythingInPlusKey = (previousTierLabel: string): string => {
  if (previousTierLabel === 'Pro') {
    return 'subscription.features.everythingOnProPlus';
  }
  return 'subscription.features.everythingInFreePlus';
};

export const UpgradePlanCard = ({
  plan,
  interval,
  previousTierLabel,
  audienceTagline,
  features,
  variant,
  ctaLabel,
  onUpgrade,
  upgradeLoading,
}: Props) => {
  const { t, i18n } = useTranslation();
  const isAnnual = interval === 'year';
  const currency = plan.currency;
  const annualAmount = plan.annual?.amount ?? 0;
  const monthlyAmount = plan.monthly?.amount ?? 0;
  const showFirstYearPromo = isAnnual && variant === 'from-free';
  const showProratedCopy = isAnnual && variant === 'from-pro';

  const monthlyEquivalent = isAnnual ? annualAmount / 12 : monthlyAmount;
  const promoValues = calcPromoValues(annualAmount, monthlyAmount);

  const handleUpgradeClick = () => {
    onUpgrade();
  };

  return (
    <Card
      variant="outlined"
      sx={{
        height: '100%',
        display: 'flex',
        flexDirection: 'column',
      }}
    >
      <CardContent
        sx={{
          flex: 1,
          display: 'flex',
          flexDirection: 'column',
          justifyContent: 'space-between',
          gap: 2,
          p: 3,
        }}
      >
        <Stack spacing={2}>
          <Box>
            <Typography variant="h5" component="h3" sx={{ fontWeight: 700 }}>
              {capitalize(plan.tier)}
            </Typography>
            <Typography variant="body2" color="text.secondary">
              {audienceTagline}
            </Typography>
          </Box>

          <Box>
            <Box sx={{ display: 'flex', alignItems: 'baseline', gap: 0.5 }}>
              <Typography variant="h4" sx={{ fontWeight: 700 }}>
                {formatCurrency(monthlyEquivalent, currency, i18n.language)}
              </Typography>
              <Typography variant="body2" color="text.secondary">
                {isAnnual ? t('subscription.upgrade.billedAnnuallyPerMonth') : t('subscription.upgrade.perMonthSuffix')}
              </Typography>
            </Box>
            {!isAnnual && (
              <Typography variant="caption" color="text.secondary">
                {t('subscription.upgrade.billedMonthly')}
              </Typography>
            )}
          </Box>

          {showFirstYearPromo && (
            <FirstYearPromoCallout
              firstYearPrice={promoValues.firstYearPrice}
              regularAnnualPrice={promoValues.regularAnnualPrice}
              savings={promoValues.savings}
              renewPrice={promoValues.renewPrice}
              currency={currency}
            />
          )}

          {showProratedCopy && (
            <Typography variant="body2" color="text.secondary">
              {t('subscription.upgrade.proratedImmediate', {
                price: formatCurrency(annualAmount, currency, i18n.language),
              })}
            </Typography>
          )}

          <Divider />

          <Box>
            <Typography variant="subtitle2" sx={{ mb: 1 }}>
              {t(getEverythingInPlusKey(previousTierLabel))}
            </Typography>
            <Stack spacing={1}>
              {groupFeatures(features).map((section) => (
                <Stack key={section.key} spacing={1}>
                  {section.label && (
                    <Typography variant="caption" sx={{ fontWeight: 600, color: 'text.secondary', mt: 0.5 }}>
                      {section.label}
                    </Typography>
                  )}
                  {section.items.map((feature) => (
                    <Box key={feature.name} sx={{ display: 'flex', alignItems: 'flex-start', gap: 1 }}>
                      <CheckIcon sx={{ fontSize: 18, color: 'success.main', mt: 0.25, flexShrink: 0 }} />
                      <Typography variant="body2">
                        {t(feature.name)}
                        {feature.description ? ` — ${t(feature.description)}` : ''}
                      </Typography>
                    </Box>
                  ))}
                </Stack>
              ))}
            </Stack>
          </Box>
        </Stack>

        <Button variant="contained" color="primary" fullWidth onClick={handleUpgradeClick} disabled={upgradeLoading}>
          {t(ctaLabel)}
        </Button>
      </CardContent>
    </Card>
  );
};
